import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-hot-toast";
import * as Yup from "yup";

const GraphicDesignerEditForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // ✅ Fetch designer by ID
  const getGraphicDesigner = useCallback(async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/admin/graphic_designer`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      );

      // Find matching designer
      const filteredDesigner = response.data.data.designers.find(
        (designer) => designer.user_id === parseInt(id)
      );

      // ✅ Keep only required fields
      if (filteredDesigner) {
        const {
          name,
          email,
          phone,
          company_name,
          website,
          gst_number,
          designation,
        } = filteredDesigner;

        return {
          name: name || "",
          email: email || "",
          phone: phone || "",
          company_name: company_name || "",
          website: website || "",
          gst_number: gst_number || "",
          designation: designation || "",
        };
      }

      return null;
    } catch (error) {
      toast.error("Failed to fetch graphic designer");
      throw error;
    }
  }, []);

  // ✅ Initial form values
  const getInitialValues = () => ({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    website: "",
    gst_number: "",
    designation: "",
  });

  // ✅ Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string()
      .matches(/^[0-9]{10,15}$/, "Phone must be 10–15 digits")
      .required("Phone is required"),
    company_name: Yup.string().required("Company name is required"),
    website: Yup.string(),
    gst_number: Yup.string().nullable(),
    designation: Yup.string().required("Designation is required"),
  });

  // ✅ Formik setup
  const formik = useFormik({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setLoading(true);
        const response = await axios.put(
          `${process.env.REACT_APP_URL}/admin/graphic_designer/${id}`,
          values,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        );

        if (response.data.success) {
          toast.success("Graphic designer updated successfully");
          navigate("/admin/graphic-designer");
        } else {
          const errorMessage =
            response.data.message || "Failed to update designer";
          toast.error(errorMessage);
        }
      } catch (error) {
        console.error("Error updating designer:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to update designer";
        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  // ✅ Fetch data for edit
  useEffect(() => {
    if (id && !initialDataLoaded) {
      const fetchDesigner = async () => {
        try {
          setLoading(true);
          const data = await getGraphicDesigner(id);
          if (data) {
            formik.setValues(data);
            setInitialDataLoaded(true);
          } else {
            toast.error("Designer not found");
            navigate("/admin/graphic-designer");
          }
        } catch {
          navigate("/admin/graphic-designer");
        } finally {
          setLoading(false);
        }
      };
      fetchDesigner();
    }
  }, [id, initialDataLoaded, getGraphicDesigner, navigate]);

  // ✅ Render
  return (
    <div className="al">
      <div className="al-head">
        <div>
          <h1 className="al-title">{id ? "Edit Graphic Designer" : "Add New Graphic Designer"}</h1>
          <p className="al-sub">Update this graphic designer's details.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => navigate(-1)} disabled={loading}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="al-card">
          <div className="al-cardhead">
            <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg></div>
            <h3 className="al-cardhead__title">Designer Details</h3>
          </div>

          <div className="al-form">
            {Object.keys(formik.values).map((key) => (
              <div key={key}>
                <label className="al-label">
                  {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                </label>
                <input
                  type="text"
                  className="al-input"
                  name={key}
                  value={formik.values[key]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched[key] && formik.errors[key] && (
                  <div className="al-err">{formik.errors[key]}</div>
                )}
              </div>
            ))}
          </div>

          <div className="al-actionbar">
            <button type="button" className="al-btn" onClick={() => navigate(-1)} disabled={loading}>Cancel</button>
            <button type="submit" className="al-btn al-btn--primary" disabled={loading}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default GraphicDesignerEditForm;
